import React, { useState, useEffect, useRef, useContext } from 'react';
import ApplicationContext from './ApplicationContext';
import { getLocalStorage } from './Utils';
import '../assets/css/scraper-runner.css';

const ScraperRunner = () => {
    const data = useContext(ApplicationContext);
    const [isRunning, setIsRunning] = useState(false);
    const [outputLines, setOutputLines] = useState([]);
    const [status, setStatus] = useState('idle'); // idle, running, completed, error
    const [elapsedTime, setElapsedTime] = useState(0);
    const [lastRunInfo, setLastRunInfo] = useState(null);
    const eventSourceRef = useRef(null);
    const timerRef = useRef(null);
    const outputEndRef = useRef(null);

    // Auto-scroll to bottom when new output arrives
    useEffect(() => {
        if (outputEndRef.current) {
            outputEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [outputLines]);

    // Timer for elapsed time
    useEffect(() => {
        if (isRunning) {
            timerRef.current = setInterval(() => {
                setElapsedTime(prev => prev + 1);
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isRunning]);

    const formatElapsedTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const addOutputLine = (line, type = 'normal') => {
        const timestamp = new Date().toLocaleTimeString();
        setOutputLines(prev => [...prev, { text: line, timestamp, type }]);
    };

    const runScraper = async () => {
        // Prevent concurrent runs
        if (isRunning) {
            return;
        }

        // Reset state
        setIsRunning(true);
        setStatus('running');
        setOutputLines([]);
        setElapsedTime(0);
        setLastRunInfo(null);

        const token = getLocalStorage('token');
        const url = `${data.BASE_URL}index.php/misc/run_scraper?token=${encodeURIComponent(token)}`;

        addOutputLine('Connecting to scraper...', 'info');

        try {
            // Create EventSource for Server-Sent Events (token passed as query param)
            const eventSource = new EventSource(url);

            eventSourceRef.current = eventSource;

            // Handle different event types
            
            // Market update events (runs first)
            eventSource.addEventListener('market_start', (event) => {
                const data = JSON.parse(event.data);
                addOutputLine(data.message, 'info');
                addOutputLine(`Started at: ${data.timestamp}`, 'info');
            });

            eventSource.addEventListener('market_output', (event) => {
                const data = JSON.parse(event.data);
                addOutputLine(`[${data.timestamp}] ${data.line}`, 'normal');
            });

            eventSource.addEventListener('market_complete', (event) => {
                const data = JSON.parse(event.data);
                addOutputLine(data.message, 'success');
                addOutputLine(`Completed at: ${data.timestamp}`, 'success');
                // Don't close connection yet - news scraper will run next
            });

            // News scraper events (runs second)
            eventSource.addEventListener('start', (event) => {
                const data = JSON.parse(event.data);
                addOutputLine(data.message, 'info');
                addOutputLine(`Started at: ${data.timestamp}`, 'info');
            });

            eventSource.addEventListener('output', (event) => {
                const data = JSON.parse(event.data);
                addOutputLine(`[${data.timestamp}] ${data.line}`, 'normal');
            });

            eventSource.addEventListener('complete', (event) => {
                const data = JSON.parse(event.data);
                addOutputLine(data.message, 'success');
                addOutputLine(`Completed at: ${data.timestamp}`, 'success');
                setStatus('completed');
                setIsRunning(false);
                setLastRunInfo({
                    status: 'success',
                    timestamp: data.timestamp,
                    duration: elapsedTime
                });
                eventSource.close();
            });

            eventSource.addEventListener('error', (event) => {
                if (event.data) {
                    const data = JSON.parse(event.data);
                    addOutputLine(`Error: ${data.message}`, 'error');
                } else {
                    addOutputLine('Connection error or scraper stopped', 'error');
                }
                setStatus('error');
                setIsRunning(false);
                eventSource.close();
            });

            // Handle connection errors
            eventSource.onerror = (error) => {
                console.error('EventSource error:', error);
                if (eventSource.readyState === EventSource.CLOSED) {
                    addOutputLine('Connection closed', 'error');
                    setStatus('error');
                    setIsRunning(false);
                }
                eventSource.close();
            };

        } catch (error) {
            console.error('Failed to start scraper:', error);
            addOutputLine(`Failed to start scraper: ${error.message}`, 'error');
            setStatus('error');
            setIsRunning(false);
        }
    };

    const stopScraper = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        addOutputLine('Stopped by user (scraper may still be running on server)', 'warning');
        setIsRunning(false);
        setStatus('idle');
    };

    const clearOutput = () => {
        setOutputLines([]);
        setElapsedTime(0);
        setStatus('idle');
    };

    const getStatusBadge = () => {
        switch (status) {
            case 'running':
                return <span className="status-badge status-running">Running</span>;
            case 'completed':
                return <span className="status-badge status-completed">Completed</span>;
            case 'error':
                return <span className="status-badge status-error">Error</span>;
            default:
                return <span className="status-badge status-idle">Idle</span>;
        }
    };

    return (
        <div className="scraper-runner-container">
            <div className="scraper-controls">
                <div className="control-row">
                    <button
                        className="scraper-button scraper-button-primary"
                        onClick={runScraper}
                        disabled={isRunning}
                    >
                        {isRunning ? 'Running...' : 'Run News Scraper'}
                    </button>
                    
                    {isRunning && (
                        <button
                            className="scraper-button scraper-button-danger"
                            onClick={stopScraper}
                        >
                            Stop
                        </button>
                    )}
                    
                    <button
                        className="scraper-button scraper-button-secondary"
                        onClick={clearOutput}
                        disabled={isRunning}
                    >
                        Clear Output
                    </button>
                </div>

                <div className="status-row">
                    <div className="status-info">
                        <strong>Status:</strong> {getStatusBadge()}
                    </div>
                    {isRunning && (
                        <div className="elapsed-time">
                            <strong>Elapsed Time:</strong> {formatElapsedTime(elapsedTime)}
                        </div>
                    )}
                    {lastRunInfo && !isRunning && (
                        <div className="last-run-info">
                            <strong>Last Run:</strong> {lastRunInfo.status} at {lastRunInfo.timestamp}
                        </div>
                    )}
                </div>

                <div className="scraper-info">
                    <p><strong>Note:</strong> This process scrapes approximately 11,000+ news articles and typically takes about 8 minutes to complete. You can monitor the progress in real-time below.</p>
                </div>
            </div>

            <div className="scraper-output-container">
                <div className="output-header">
                    <strong>Console Output:</strong>
                    {isRunning && <span className="output-live-indicator">● LIVE</span>}
                </div>
                <div className="scraper-output">
                    {outputLines.length === 0 ? (
                        <div className="output-placeholder">Output will appear here when scraper runs...</div>
                    ) : (
                        outputLines.map((line, index) => (
                            <div key={index} className={`output-line output-${line.type}`}>
                                {line.text}
                            </div>
                        ))
                    )}
                    <div ref={outputEndRef} />
                </div>
            </div>
        </div>
    );
};

export default ScraperRunner;
